import { useEffect, useState } from 'react';
import * as React from 'react';
import type { TransferJobTaskReactiveMapState } from '@frameio/graphql-client/client-schema/transfers/state/transferJobTasks';
import { plural } from '@frameio/i18n';
import { useGetAssetForAssetTitleQuery } from '@frameio/shared/navigation/queries/generated/GetAssetForAssetTitle';
import { AlertDialog, Box, ScrollContainer, Stack, Text } from '@frameio/vapor';
import type { UploadItemType } from '../../types/uploader';
import TEXT from './generated/TEXT/FailedItemsDialog';

const ITEM_LIMIT = 30;

TEXT.DEFINE(
  {
    /** Dialog title showing count of items that failed to upload */
    failedItemsText: (itemCount: number): string =>
      `${plural(itemCount, {
        one: '1 Item',
        other: `${itemCount} Items`,
      })} failed to upload`,
    /** Dialog title showing count of unsupported items */
    unsupportedItemsText: (itemCount: number): string =>
      `${plural(itemCount, {
        one: '1 Item',
        other: `${itemCount} Items`,
      })} not supported for upload`,
    /** Dialog description explaining why items can't be uploaded */
    details: 'Some unsupported items cannot be uploaded.',
    /** Link text to learn more about supported items */
    detailsLinkText: 'Learn more ↗',
    /** Text showing count of additional items not displayed */
    additionalItems: (itemCount: number): string =>
      `+ ${plural(itemCount, {
        one: '1 Item',
        other: `${itemCount} Items`,
      })}`,
    /** Confirm button text to dismiss the dialog */
    confirmButton: 'OK',
  },
  { plural }
);

type FailedItemsDialogProps =
  | {
      variant: 'unsupported';
      items: UploadItemType[];
    }
  | {
      variant: 'uploadFailed';
      items: TransferJobTaskReactiveMapState[];
    };

const AssetTitle = ({ id }: { id: string }) => {
  const assetName = useGetAssetForAssetTitleQuery({
    variables: { assetId: id },
  })?.data?.account?.asset?.name;

  return (
    <Box paddingY={6}>
      <Text color="secondary" variant="body">
        {assetName}
      </Text>
    </Box>
  );
};

function isUploadItemType(
  job: UploadItemType | TransferJobTaskReactiveMapState
): job is UploadItemType {
  return !!(job as UploadItemType).id;
}

const AssetTitles = ({
  items,
}: {
  items: UploadItemType[] | TransferJobTaskReactiveMapState[];
}) => {
  return (
    items
      // Filter out items which don't have `id` or `assetId`,
      // since we need 1 of these values to render the UI.
      .filter((item) => {
        if (isUploadItemType(item)) {
          return true;
        }
        return !!item.assetId;
      })
      // Handle UploadItemType elements - these have `id`s
      .map((item) => {
        if (isUploadItemType(item)) {
          return (
            <Box key={item.id} paddingY={6}>
              <Text color="secondary" variant="body">
                {item.name}
              </Text>
            </Box>
          );
        }
        // Handle (TransferJobTaskReactiveMapState) - these have `assetId`s
        return <AssetTitle key={item.assetId} id={item.assetId!} />;
      })
  );
};

export const FailedItemsDialog = ({
  items,
  variant = 'unsupported',
}: FailedItemsDialogProps): React.ReactElement<any> => {
  const [isShowing, setIsShowing] = useState(false);
  const additionalItems = items.length - ITEM_LIMIT;

  const ItemsContainer = (
    <ScrollContainer
      enableBottomBorder={true}
      enableTopBorder={true}
      maxHeight="30vh"
      paddingX={0}
      paddingY={8}
      topBorderColor="primary"
    >
      <Stack alignX="start">
        <AssetTitles items={items} />
        {items.length > ITEM_LIMIT && (
          <Box paddingY={6}>
            <Text color="tertiary">
              {TEXT.additionalItems(additionalItems)}
            </Text>
          </Box>
        )}
      </Stack>
    </ScrollContainer>
  );

  const DialogTitle = (
    <>
      {variant === 'unsupported' ? (
        <>
          <Text fontWeight="semiBold" variant="primary">
            {TEXT.unsupportedItemsText(items.length)}
          </Text>
          <Text color="secondary" display="inline" variant="secondary">
            {TEXT.details}
          </Text>
          <Box display="inline-block" paddingLeft={4}>
            <Text display="inline" variant="secondary">
              {TEXT.detailsLinkText}
            </Text>
          </Box>
        </>
      ) : (
        <Text fontWeight="semiBold" variant="primary">
          {TEXT.failedItemsText(items.length)}
        </Text>
      )}
    </>
  );

  useEffect(() => {
    if (items.length > 0) {
      setIsShowing(true);
    }
  }, [items]);

  return (
    <AlertDialog
      confirmText={TEXT.confirmButton}
      details={ItemsContainer}
      isShowing={isShowing}
      onConfirm={() => setIsShowing(false)}
      title={DialogTitle}
      variant="neutral"
    />
  );
};
